import React, { useState, useEffect } from "react";
import api from "../api";

const SuperAdminDashboard = () => {
  const [tab, setTab] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [totalTenants, setTotalTenants] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [filterPlano, setFilterPlano] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [usuarios, setUsuarios] = useState([]);
  const [inviteCodes, setInviteCodes] = useState([]);
  const [inviteEnabled, setInviteEnabled] = useState(true);
  const [showCreateInvite, setShowCreateInvite] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({ nome: "", slug: "", plano: "completo", owner_email: "" });
  const [editingTenant, setEditingTenant] = useState(null);
  const [msg, setMsg] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => { loadStats(); }, []);
  useEffect(() => { loadTenants(); }, [page, search, filterPlano, filterStatus]);

  const loadStats = async () => {
    try {
      const res = await api.get("/superadmin/stats");
      setStats(res.data);
    } catch { setError("Erro ao carregar stats."); }
  };

  const loadTenants = async () => {
    try {
      const params = { page, per_page: 15 };
      if (search) params.search = search;
      if (filterPlano) params.plano = filterPlano;
      if (filterStatus) params.status = filterStatus;
      const res = await api.get("/superadmin/tenants", { params });
      setTenants(res.data.tenants);
      setTotalTenants(res.data.total);
    } catch {}
  };

  const loadUsuarios = async () => {
    try {
      const res = await api.get("/superadmin/usuarios", { params: { per_page: 100 } });
      setUsuarios(res.data.usuarios);
    } catch {}
  };

  const loadInvites = async () => {
    try {
      const [cfg, codes] = await Promise.all([
        api.get("/superadmin/config"),
        api.get("/superadmin/invite-codes"),
      ]);
      setInviteEnabled(!!cfg.data.invite_code_enabled);
      setInviteCodes(codes.data);
    } catch {}
  };

  const toggleInviteEnabled = async () => {
    const novo = !inviteEnabled;
    setInviteEnabled(novo);
    try { await api.patch("/superadmin/config", { invite_code_enabled: novo }); }
    catch { setInviteEnabled(!novo); }
  };

  const handleCreateInvite = async (form) => {
    try {
      const res = await api.post("/superadmin/invite-codes", form);
      setInviteCodes(prev => [res.data, ...prev]);
      setShowCreateInvite(false);
      setMsg(`Código criado: ${res.data.code}`);
    } catch (err) {
      setError(err.response?.data?.erro || "Erro ao criar código.");
    }
  };

  const handleDesativarInvite = async (id) => {
    setInviteCodes(prev => prev.map(c => c.id === id ? { ...c, ativo: false, is_valid: false } : c));
    try { await api.patch(`/superadmin/invite-codes/${id}`, { ativo: false }); }
    catch { loadInvites(); }
  };

  // Reativar NAO e "ligar o ativo": a coluna mostra se o codigo e UTILIZAVEL, e
  // na maioria das vezes ele aparece como "Nao" por estar ESGOTADO (1/1), com o
  // `ativo` ja true. Quem decide o que mudar e o servidor (backend/convites.py);
  // aqui so gravamos a linha que ele devolve, senao a tela mentiria.
  const handleReativarInvite = async (id) => {
    try {
      const { data } = await api.post(`/superadmin/invite-codes/${id}/reativar`);
      setInviteCodes(prev => prev.map(c => (c.id === id ? data : c)));
      setMsg('Código reativado.');
    } catch {
      setError('Não consegui reativar o código.');
      loadInvites();
    }
  };

  const handleDeleteInvite = async (id) => {
    setInviteCodes(prev => prev.filter(c => c.id !== id));
    try { await api.delete(`/superadmin/invite-codes/${id}`); }
    catch { loadInvites(); }
  };

  const handleUpdateTenant = async (id, data) => {
    setMsg(null); setError(null);
    try {
      await api.put(`/superadmin/tenants/${id}`, data);
      setMsg("Tenant atualizado!");
      loadTenants();
      loadStats();
      setEditingTenant(null);
    } catch (err) {
      setError(err.response?.data?.erro || "Erro ao atualizar.");
    }
  };

  const handleCreateTenant = async () => {
    setMsg(null); setError(null);
    try {
      await api.post("/superadmin/tenants", createForm);
      setMsg("Tenant criado!");
      setShowCreateModal(false);
      setCreateForm({ nome: "", slug: "", plano: "completo", owner_email: "" });
      loadTenants();
      loadStats();
    } catch (err) {
      setError(err.response?.data?.erro || "Erro ao criar.");
    }
  };

  const handleMigrate = async (id, slug) => {
    setMsg(null);
    try {
      await api.post(`/superadmin/tenants/${id}/migrate`);
      setMsg(`Migrations rodadas em ${slug}`);
    } catch (err) {
      setError(err.response?.data?.erro || "Erro nas migrations.");
    }
  };

  const handleWipeData = async (id, slug) => {
    setMsg(null); setError(null);
    const confirmInput = window.prompt(
      `ATENÇÃO: isto vai apagar TODOS os dados operacionais do tenant "${slug}".\n` +
      `Pedidos, fichas, produtos, recheios, finanças — tudo zerado.\n` +
      `Schema e usuário dono permanecem.\n\n` +
      `Para confirmar, digite o slug: ${slug}`
    );
    if (confirmInput !== slug) {
      if (confirmInput !== null) setError('Confirmação não bate. Operação cancelada.');
      return;
    }
    try {
      const res = await api.post(`/superadmin/tenants/${id}/wipe-data`, { confirm: slug });
      setMsg(`Wipe concluído em ${slug}: ${res.data.truncated.length} tabelas truncadas, ${res.data.presets_cleaned.length} com presets preservados.`);
    } catch (err) {
      setError(err.response?.data?.erro || 'Erro no wipe.');
    }
  };

  const handleToggleSuperadmin = async (userId, current) => {
    setUsuarios(prev => prev.map(u => u.id === userId ? { ...u, is_superadmin: !current } : u));
    try { await api.put(`/superadmin/usuarios/${userId}/superadmin`, { is_superadmin: !current }); }
    catch { loadUsuarios(); }
  };

  return (
    <div style={{ maxWidth: 960, margin: "0 auto", padding: "24px 16px" }}>
      <h2 style={{ marginBottom: 4 }}>Super Admin</h2>
      <p style={{ color: "#888", fontSize: 14, marginBottom: 20 }}>Gerenciamento global de tenants</p>

      {msg && <div style={s.success}>{msg}</div>}
      {error && <div style={s.error}>{error}</div>}

      {/* Tabs */}
      <div style={s.tabs}>
        {["dashboard", "tenants", "usuarios", "convites"].map((t) => (
          <button
            key={t}
            onClick={() => { setTab(t); if (t === "usuarios") loadUsuarios(); if (t === "convites") loadInvites(); }}
            style={tab === t ? { ...s.tab, ...s.tabActive } : s.tab}
          >
            {t === "dashboard" ? "Dashboard" : t === "tenants" ? "Tenants" : t === "usuarios" ? "Usuários" : "Convites"}
          </button>
        ))}
      </div>

      {/* Dashboard */}
      {tab === "dashboard" && stats && (
        <>
          {/* Billing / MRR */}
          {stats.billing && (
            <>
              <h3 style={s.sectionTitle}>Receita recorrente</h3>
              <div style={s.grid}>
                <StatCard
                  label="MRR"
                  value={`R$ ${stats.billing.mrr.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
                  color="#16a34a"
                  sub={`${stats.billing.paying_subscribers} assinante(s) × R$ ${stats.billing.plan_valor.toFixed(2)}`}
                />
                <StatCard
                  label="ARR (projeção)"
                  value={`R$ ${stats.billing.arr.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
                  color="#0ea5e9"
                  sub="MRR × 12"
                />
                <StatCard
                  label="Trials ativos"
                  value={stats.billing.trials}
                  color="#a855f7"
                />
                <StatCard
                  label="Em risco (past due)"
                  value={stats.billing.at_risk}
                  color={stats.billing.at_risk > 0 ? "#e74c3c" : "#94a3b8"}
                  sub="Pagamento falhou — em grace period"
                />
                <StatCard
                  label="Churn 30d"
                  value={`${stats.billing.churn_rate_30d.toFixed(1)}%`}
                  color={stats.billing.churn_rate_30d > 5 ? "#e74c3c" : "#64748b"}
                  sub={`${stats.billing.canceled_last_30d} cancelamento(s)`}
                />
                <StatCard
                  label="Conversão 30d"
                  value={`${stats.billing.conversion_rate_30d.toFixed(1)}%`}
                  color="#f59e0b"
                  sub={`${stats.billing.converted_last_30d} de ${stats.billing.new_last_30d} novos`}
                />
              </div>
            </>
          )}

          <h3 style={s.sectionTitle}>Visão geral</h3>
          <div style={s.grid}>
            <StatCard label="Total tenants" value={stats.total_tenants} color="#3498db" />
            <StatCard label="Ativos" value={stats.active_tenants} color="#27ae60" />
            <StatCard label="Inativos" value={stats.inactive_tenants} color="#e74c3c" />
            <StatCard label="Total usuários" value={stats.total_users} color="#8e44ad" />
            {Object.entries(stats.by_plan || {}).map(([plan, count]) => (
              <StatCard key={plan} label={`Plano ${plan}`} value={count} color="#f39c12" />
            ))}
          </div>

          {stats.by_status && (
            <>
              <h3 style={s.sectionTitle}>Por status de assinatura</h3>
              <div style={s.grid}>
                {Object.entries(stats.by_status).map(([st, count]) => (
                  <StatCard
                    key={st}
                    label={st}
                    value={count}
                    color={
                      st === 'active' ? '#16a34a' :
                      st === 'trial' ? '#a855f7' :
                      st === 'past_due' ? '#e11d48' :
                      st === 'canceled' ? '#64748b' :
                      '#0ea5e9'
                    }
                  />
                ))}
              </div>
            </>
          )}
        </>
      )}

      {/* Tenants */}
      {tab === "tenants" && (
        <div>
          <div style={s.toolbar}>
            <input
              placeholder="Buscar por nome ou slug..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              style={s.searchInput}
            />
            <select value={filterPlano} onChange={(e) => { setFilterPlano(e.target.value); setPage(1); }} style={s.select}>
              <option value="">Todos os planos</option>
              <option value="completo">Completo</option>
            </select>
            <select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }} style={s.select}>
              <option value="">Todos</option>
              <option value="ativo">Ativos</option>
              <option value="inativo">Inativos</option>
            </select>
            <button onClick={() => setShowCreateModal(true)} style={s.btnPrimary}>+ Novo Tenant</button>
          </div>

          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>ID</th>
                <th style={s.th}>Nome</th>
                <th style={s.th}>Slug</th>
                <th style={s.th}>Plano</th>
                <th style={s.th}>Status</th>
                <th style={s.th}>Criado em</th>
                <th style={s.th}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {tenants.map((t) => (
                <tr key={t.id}>
                  <td style={s.td}>{t.id}</td>
                  <td style={s.td}>{t.nome}</td>
                  <td style={s.td}><code>{t.slug}</code></td>
                  <td style={s.td}>
                    <span style={{ ...s.badge, background: planColor(t.plano) }}>{t.plano_label}</span>
                  </td>
                  <td style={s.td}>
                    <span style={{ color: t.ativo ? "#27ae60" : "#e74c3c", fontWeight: 600 }}>
                      {t.ativo ? "Ativo" : "Inativo"}
                    </span>
                  </td>
                  <td style={s.td}>{t.created_at ? new Date(t.created_at).toLocaleDateString("pt-BR") : "-"}</td>
                  <td style={s.td}>
                    <button onClick={() => setEditingTenant(t)} style={s.btnSmall}>Editar</button>
                    <button onClick={() => handleMigrate(t.id, t.slug)} style={{ ...s.btnSmall, background: "#3498db" }}>Migrate</button>
                    <button onClick={() => handleWipeData(t.id, t.slug)} style={{ ...s.btnSmall, background: "#7f1d1d" }}>Wipe</button>
                    <button
                      onClick={() => handleUpdateTenant(t.id, { ativo: !t.ativo })}
                      style={{ ...s.btnSmall, background: t.ativo ? "#e74c3c" : "#27ae60" }}
                    >
                      {t.ativo ? "Desativar" : "Ativar"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={s.pagination}>
            <span style={{ fontSize: 13, color: "#888" }}>{totalTenants} tenant(s)</span>
            <div>
              <button disabled={page <= 1} onClick={() => setPage(page - 1)} style={s.pageBtn}>Anterior</button>
              <span style={{ margin: "0 12px", fontSize: 14 }}>Página {page}</span>
              <button disabled={tenants.length < 15} onClick={() => setPage(page + 1)} style={s.pageBtn}>Próxima</button>
            </div>
          </div>
        </div>
      )}

      {/* Usuários */}
      {tab === "usuarios" && (
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>Email</th>
              <th style={s.th}>Nome</th>
              <th style={s.th}>Tenant</th>
              <th style={s.th}>Owner</th>
              <th style={s.th}>Superadmin</th>
            </tr>
          </thead>
          <tbody>
            {usuarios.map((u) => (
              <tr key={u.id}>
                <td style={s.td}>{u.email}</td>
                <td style={s.td}>{u.display_name || "-"}</td>
                <td style={s.td}>{u.tenant_nome || "-"} <code style={{ fontSize: 11 }}>{u.tenant_slug}</code></td>
                <td style={s.td}>{u.is_owner ? "Sim" : "-"}</td>
                <td style={s.td}>
                  <button
                    onClick={() => handleToggleSuperadmin(u.id, u.is_superadmin)}
                    style={{ ...s.btnSmall, background: u.is_superadmin ? "#e74c3c" : "#27ae60" }}
                  >
                    {u.is_superadmin ? "Remover SA" : "Tornar SA"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Convites */}
      {tab === "convites" && (
        <div>
          <div style={{ ...s.card, padding: '16px 20px', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700 }}>Campo "Código de convite" no cadastro</div>
              <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
                Quando desligado, usuárias não verão o campo na tela de registro.
              </div>
            </div>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={inviteEnabled} onChange={toggleInviteEnabled} style={{ width: 18, height: 18, cursor: 'pointer' }} />
              <span style={{ fontSize: 13, color: inviteEnabled ? '#27ae60' : '#e74c3c', fontWeight: 600 }}>
                {inviteEnabled ? 'Habilitado' : 'Desabilitado'}
              </span>
            </label>
          </div>

          <div style={s.toolbar}>
            <div style={{ flex: 1, fontSize: 13, color: '#555' }}>
              {inviteCodes.length} código(s) cadastrado(s)
            </div>
            <button onClick={() => setShowCreateInvite(true)} style={s.btnPrimary}>+ Novo código</button>
          </div>

          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Código</th>
                <th style={s.th}>Label</th>
                <th style={s.th}>Usos</th>
                <th style={s.th}>Dias</th>
                <th style={s.th}>Validade</th>
                <th style={s.th}>Ativo</th>
                <th style={s.th}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {inviteCodes.map(c => (
                <tr key={c.id}>
                  <td style={s.td}>
                    <code style={{ background: '#f4f4f4', padding: '2px 6px', borderRadius: 4, fontSize: 13 }}>{c.code}</code>
                    <button onClick={() => { navigator.clipboard?.writeText(c.code); setMsg('Copiado!'); }} style={{ ...s.btnSmall, marginLeft: 6, background: '#95a5a6' }}>Copiar</button>
                  </td>
                  <td style={s.td}>{c.label || '—'}</td>
                  <td style={s.td}>{c.uses}/{c.max_uses}</td>
                  <td style={s.td}>{c.trial_days}</td>
                  <td style={s.td}>{c.valid_until ? new Date(c.valid_until).toLocaleDateString('pt-BR') : 'Sem validade'}</td>
                  <td style={s.td}>
                    <span style={{ color: c.is_valid ? '#27ae60' : '#e74c3c', fontWeight: 600, fontSize: 13 }}>
                      {c.is_valid ? 'Sim' : 'Não'}
                    </span>
                  </td>
                  <td style={s.td}>
                    {c.is_valid ? (
                      <button onClick={() => handleDesativarInvite(c.id)} style={{ ...s.btnSmall, background: '#e74c3c' }}>
                        Desativar
                      </button>
                    ) : (
                      <button onClick={() => handleReativarInvite(c.id)} style={{ ...s.btnSmall, background: '#27ae60' }}
                        title={c.uses >= c.max_uses ? 'Libera mais um uso' : 'Volta a valer'}>
                        Reativar
                      </button>
                    )}
                    <button onClick={() => handleDeleteInvite(c.id)} style={{ ...s.btnSmall, background: '#95a5a6' }}>Excluir</button>
                  </td>
                </tr>
              ))}
              {inviteCodes.length === 0 && (
                <tr><td colSpan={7} style={{ ...s.td, textAlign: 'center', color: '#888', padding: 32 }}>Nenhum código cadastrado.</td></tr>
              )}
            </tbody>
          </table>

          {showCreateInvite && (
            <CreateInviteModal onSave={handleCreateInvite} onClose={() => setShowCreateInvite(false)} />
          )}
        </div>
      )}

      {/* Modal Criar Tenant */}
      {showCreateModal && (
        <div style={s.overlay}>
          <div style={s.modal}>
            <h3 style={{ marginBottom: 16 }}>Novo Tenant</h3>
            <div style={s.field}>
              <label style={s.label}>Nome</label>
              <input value={createForm.nome} onChange={(e) => setCreateForm({ ...createForm, nome: e.target.value })} style={s.input} />
            </div>
            <div style={s.field}>
              <label style={s.label}>Slug</label>
              <input value={createForm.slug} onChange={(e) => setCreateForm({ ...createForm, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })} style={s.input} />
            </div>
            <div style={s.field}>
              <label style={s.label}>Plano</label>
              <select value={createForm.plano} onChange={(e) => setCreateForm({ ...createForm, plano: e.target.value })} style={s.input}>
                <option value="completo">Completo</option>
              </select>
            </div>
            <div style={s.field}>
              <label style={s.label}>Email do owner (opcional)</label>
              <input value={createForm.owner_email} onChange={(e) => setCreateForm({ ...createForm, owner_email: e.target.value })} style={s.input} />
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
              <button onClick={handleCreateTenant} style={s.btnPrimary}>Criar</button>
              <button onClick={() => setShowCreateModal(false)} style={{ ...s.btnSmall, background: "#999" }}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Editar Tenant */}
      {editingTenant && (
        <EditTenantModal
          tenant={editingTenant}
          onSave={(data) => handleUpdateTenant(editingTenant.id, data)}
          onClose={() => setEditingTenant(null)}
        />
      )}
    </div>
  );
};

const CreateInviteModal = ({ onSave, onClose }) => {
  const [code, setCode] = useState("");
  const [label, setLabel] = useState("");
  const [maxUses, setMaxUses] = useState(1);
  const [trialDays, setTrialDays] = useState(30);
  const [validUntil, setValidUntil] = useState("");

  const handleSubmit = () => {
    const payload = {
      code: code.trim().toUpperCase() || undefined,
      label: label.trim() || undefined,
      max_uses: parseInt(maxUses) || 1,
      trial_days: parseInt(trialDays) || 30,
    };
    if (validUntil) payload.valid_until = new Date(validUntil).toISOString();
    onSave(payload);
  };

  return (
    <div style={s.overlay}>
      <div style={s.modal}>
        <h3 style={{ marginBottom: 16 }}>Novo código de convite</h3>
        <div style={s.field}>
          <label style={s.label}>Código <span style={{ color: '#999', fontSize: 11, fontWeight: 400 }}>(deixe vazio para gerar automaticamente)</span></label>
          <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="METRIA-XXXXXX" style={s.input} />
        </div>
        <div style={s.field}>
          <label style={s.label}>Label <span style={{ color: '#999', fontSize: 11, fontWeight: 400 }}>(referência interna)</span></label>
          <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Ex: Teste beta — confeiteiras indicadas" style={s.input} />
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ ...s.field, flex: 1 }}>
            <label style={s.label}>Nº de usos</label>
            <input type="number" min="1" value={maxUses} onChange={(e) => setMaxUses(e.target.value)} style={s.input} />
          </div>
          <div style={{ ...s.field, flex: 1 }}>
            <label style={s.label}>Dias de trial</label>
            <input type="number" min="1" value={trialDays} onChange={(e) => setTrialDays(e.target.value)} style={s.input} />
          </div>
        </div>
        <div style={s.field}>
          <label style={s.label}>Válido até <span style={{ color: '#999', fontSize: 11, fontWeight: 400 }}>(opcional)</span></label>
          <input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} style={s.input} />
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
          <button onClick={handleSubmit} style={s.btnPrimary}>Criar</button>
          <button onClick={onClose} style={{ ...s.btnSmall, background: "#999" }}>Cancelar</button>
        </div>
      </div>
    </div>
  );
};

// Modulos que se ligam conta a conta. Lista escrita a mao de proposito: cada
// linha aqui e uma decisao de produto, e a tela precisa dizer o que a
// confeitaria vai ver quando a chave virar — nao so o nome interno.
const MODULOS = [
  {
    chave: 'etiquetas',
    titulo: 'Validade e etiquetas',
    descricao: 'Menu "Validade", impressao de etiqueta 60x40 e baixa por QR.',
  },
];

const EditTenantModal = ({ tenant, onSave, onClose }) => {
  const [nome, setNome] = useState(tenant.nome);
  const [plano, setPlano] = useState(tenant.plano);
  const [modulos, setModulos] = useState(() => ({ ...(tenant.modulos || {}) }));

  return (
    <div style={s.overlay}>
      <div style={s.modal}>
        <h3 style={{ marginBottom: 16 }}>Editar: {tenant.slug}</h3>
        <div style={s.field}>
          <label style={s.label}>Nome</label>
          <input value={nome} onChange={(e) => setNome(e.target.value)} style={s.input} />
        </div>
        <div style={s.field}>
          <label style={s.label}>Plano</label>
          <select value={plano} onChange={(e) => setPlano(e.target.value)} style={s.input}>
            <option value="completo">Completo</option>
          </select>
        </div>
        <div style={s.field}>
          <label style={s.label}>Modulos</label>
          {MODULOS.map((m) => (
            <label key={m.chave} style={{ display: "flex", gap: 8, alignItems: "flex-start", marginTop: 6, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={Boolean(modulos[m.chave])}
                onChange={(e) => setModulos({ ...modulos, [m.chave]: e.target.checked })}
                style={{ marginTop: 3 }}
              />
              <span>
                <span style={{ fontWeight: 600 }}>{m.titulo}</span>
                <span style={{ display: "block", fontSize: 12, color: "#888" }}>{m.descricao}</span>
              </span>
            </label>
          ))}
          <div style={{ fontSize: 11, color: "#aaa", marginTop: 6 }}>
            Aparece para a confeitaria no proximo carregamento do app — sem precisar sair e entrar.
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
          <button onClick={() => onSave({ nome, plano, modulos })} style={s.btnPrimary}>Salvar</button>
          <button onClick={onClose} style={{ ...s.btnSmall, background: "#999" }}>Cancelar</button>
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ label, value, color, sub }) => (
  <div style={{ ...s.card, borderLeft: `4px solid ${color}` }}>
    <div style={{ fontSize: 28, fontWeight: 700, color }}>{value}</div>
    <div style={{ fontSize: 13, color: "#888", marginTop: 4 }}>{label}</div>
    {sub && <div style={{ fontSize: 11, color: "#aaa", marginTop: 3 }}>{sub}</div>}
  </div>
);

const planColor = (p) => {
  if (p === "completo") return "#27ae60";
  return "#95a5a6";
};

const s = {
  tabs: { display: "flex", gap: 4, marginBottom: 20 },
  tab: {
    padding: "8px 20px", borderRadius: 8, border: "1px solid #ddd",
    background: "#fff", cursor: "pointer", fontSize: 14, fontWeight: 500,
  },
  tabActive: { background: "#2c3e50", color: "#fff", borderColor: "#2c3e50" },
  sectionTitle: { fontSize: 14, fontWeight: 700, color: "#475569", margin: "20px 0 10px", textTransform: "uppercase", letterSpacing: 0.5 },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 12 },
  card: {
    background: "#fff", borderRadius: 10, padding: "16px 20px",
    boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
  },
  toolbar: { display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" },
  searchInput: {
    flex: 1, minWidth: 200, padding: "8px 12px", borderRadius: 8,
    border: "1px solid #ddd", fontSize: 14,
  },
  select: { padding: "8px 12px", borderRadius: 8, border: "1px solid #ddd", fontSize: 14 },
  table: { width: "100%", borderCollapse: "collapse", background: "#fff", borderRadius: 10, overflow: "hidden", boxShadow: "0 2px 6px rgba(0,0,0,0.06)" },
  th: { textAlign: "left", padding: "10px 12px", background: "#f8f9fa", fontSize: 13, fontWeight: 600, color: "#555", borderBottom: "1px solid #eee" },
  td: { padding: "10px 12px", fontSize: 14, borderBottom: "1px solid #f0f0f0" },
  badge: { padding: "3px 10px", borderRadius: 10, color: "#fff", fontSize: 12, fontWeight: 600 },
  btnPrimary: {
    padding: "8px 20px", borderRadius: 8, border: "none",
    background: "#2c3e50", color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer",
  },
  btnSmall: {
    padding: "4px 12px", borderRadius: 6, border: "none",
    background: "#f39c12", color: "#fff", fontSize: 12, fontWeight: 600,
    cursor: "pointer", marginRight: 4,
  },
  pageBtn: {
    padding: "6px 14px", borderRadius: 6, border: "1px solid #ddd",
    background: "#fff", fontSize: 13, cursor: "pointer",
  },
  pagination: { display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12 },
  overlay: {
    position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
    background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center",
    justifyContent: "center", zIndex: 1000,
  },
  modal: {
    background: "#fff", borderRadius: 16, padding: "32px",
    width: '100%', maxWidth: 400, maxHeight: "80vh", overflow: "auto",
    boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
  },
  field: { marginBottom: 12 },
  label: { display: "block", fontSize: 13, fontWeight: 600, color: "#555", marginBottom: 4 },
  input: {
    width: "100%", padding: "8px 12px", borderRadius: 8,
    border: "1px solid #ddd", fontSize: 14, boxSizing: "border-box",
  },
  success: { background: "#eafaf1", color: "#27ae60", padding: "10px 14px", borderRadius: 8, marginBottom: 16, fontSize: 14 },
  error: { background: "#fdecea", color: "#c0392b", padding: "10px 14px", borderRadius: 8, marginBottom: 16, fontSize: 14 },
};

export default SuperAdminDashboard;
